import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Calculator, Euro } from 'lucide-react';
import { getParticipants } from '../lib/services/participantsService';
import { createDepensePartagee } from '../lib/services/depensesPartageesService';
import { Participant, CreateParticipationData } from '../types/shared-expenses';
import { cleanNumericInput, isValidNumericValue } from '../lib/utils/numberUtils';

const AddSharedExpense: React.FC = () => {
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [montantTotal, setMontantTotal] = useState('');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0] || '');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantsMontants, setParticipantsMontants] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  useEffect(() => {
    loadParticipants();
  }, []);

  const loadParticipants = async () => {
    setLoadingParticipants(true);
    try {
      const { data, error } = await getParticipants();
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setParticipants(data);
        
        // Initialiser les montants à 0 pour chaque participant
        const montants: Record<string, string> = {};
        data.forEach(participant => {
          montants[participant.id] = '0';
        });
        setParticipantsMontants(montants);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des participants:', error);
      alert('Impossible de charger la liste des participants.');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleMontantChange = (montant: string, participantId: string) => {
    // Nettoyer la valeur pour n'accepter que les nombres et le point décimal
    const cleanedValue = montant.replace(/[^0-9.]/g, '');
    // Supprimer les zéros non significatifs
    const finalValue = cleanNumericInput(cleanedValue);
    
    // Mettre à jour le montant pour ce participant
    setParticipantsMontants(prev => ({
      ...prev,
      [participantId]: finalValue
    }));
  };

  // Fonction pour calculer automatiquement le montant de l'autre participant
  const calculerMontantAutre = () => {
    if (!montantTotal || isNaN(parseFloat(montantTotal)) || parseFloat(montantTotal) <= 0) {
      alert('Veuillez d\'abord saisir un montant total valide.');
      return;
    }

    if (participants.length < 2) {
      alert('Il faut au moins 2 participants pour utiliser cette fonction.');
      return;
    }

    // Prendre le premier participant qui a un montant > 0
    const participantAvecMontant = participants.find(p => {
      const montant = participantsMontants[p.id];
      return montant && parseFloat(montant) > 0;
    });

    if (!participantAvecMontant) {
      alert('Veuillez d\'abord saisir un montant pour un participant.');
      return;
    }

    const total = parseFloat(montantTotal);
    const montantPremier = parseFloat(participantsMontants[participantAvecMontant.id] || '0');
    
    // Calculer le montant de l'autre participant
    const montantAutre = (total - montantPremier).toFixed(2);
    
    // Trouver l'autre participant
    const autreParticipant = participants.find(p => p.id !== participantAvecMontant.id);
    
    if (autreParticipant) {
      // Mettre à jour le montant de l'autre participant
      setParticipantsMontants(prev => ({
        ...prev,
        [autreParticipant.id]: montantAutre
      }));
    }
  };

  const calculerTotal = () => {
    let total = 0;
    
    Object.values(participantsMontants).forEach(montant => {
      const valeur = parseFloat(montant);
      if (!isNaN(valeur)) {
        total += valeur;
      }
    });
    
    return total.toFixed(2);
  };

  const handleSaveDepense = () => {
    if (loading) return;
    
    // Validation des champs
    if (!description.trim()) {
      alert('Veuillez saisir une description.');
      return;
    }
    
    if (!isValidNumericValue(montantTotal)) {
      alert('Le montant total doit être un nombre valide.');
      return;
    }
    
    const montantTotalValue = parseFloat(montantTotal);
    if (montantTotalValue <= 0) {
      alert('Le montant total doit être supérieur à 0.');
      return;
    }
    
    // Vérifier que le total des montants correspond au montant total
    const totalPaye = parseFloat(calculerTotal());
    const total = parseFloat(montantTotal);
    
    if (Math.abs(totalPaye - total) > 0.01) {
      alert(
        `Totaux incorrects : la somme payée (${totalPaye.toFixed(2)} €) ne correspond pas au montant total (${total.toFixed(2)} €).\nCorrigez les montants avant d'enregistrer.`
      );
      return;
    }
    
    saveDepense();
  };

  const saveDepense = async () => {
    setLoading(true);
    try {
      // Créer la dépense partagée
      const depense = {
        date,
        description,
        montant_total: parseFloat(montantTotal)
      };
      
      // Créer les participations
      const participations: CreateParticipationData[] = [];
      
      participants.forEach(participant => {
        const montant = participantsMontants[participant.id];
        
        // Si le montant est vide, non numérique ou 0, on l'enregistre comme 0
        if (!montant || isNaN(parseFloat(montant)) || parseFloat(montant) === 0) {
          participations.push({
            participant_id: participant.id,
            montant_paye: 0
          });
        } else {
          // Sinon, on enregistre le montant normal (> 0)
          participations.push({
            participant_id: participant.id,
            montant_paye: parseFloat(montant)
          });
        }
      });
      
      // Enregistrer la dépense et ses participations
      const { error } = await createDepensePartagee(depense, participations);
      
      if (error) throw error;
      
      alert('La dépense partagée a été enregistrée avec succès.');
      navigate('/shared-expenses');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la dépense:', error);
      
      // Afficher un message d'erreur plus détaillé
      let errorMessage = 'Impossible d\'enregistrer la dépense partagée.';
      
      if (error instanceof Error) {
        if (error.message.includes('Utilisateur non connecté')) {
          errorMessage = 'Vous devez être connecté pour créer une dépense partagée.';
        } else if (error.message.includes('participants')) {
          errorMessage = 'Erreur avec les participants. Vérifiez que tous les participants existent.';
        } else if (error.message.includes('depenses_partagees')) {
          errorMessage = 'Erreur lors de la création de la dépense. Vérifiez que vous êtes connecté.';
        } else {
          errorMessage = `Erreur: ${error.message}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingParticipants) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-dark-300">Chargement des participants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/shared-expenses')}
          className="p-2 text-dark-300 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white">Nouvelle dépense partagée</h1>
          <p className="text-dark-300 mt-2">Ajoutez une nouvelle dépense à partager entre participants</p>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-white">Enregistrement en cours...</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Formulaire principal */}
        <div className="space-y-6">
          <div className="bg-dark-900 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Informations générales</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description de la dépense"
                  className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Montant total
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={montantTotal}
                    onChange={(e) => {
                      const cleaned = cleanNumericInput(e.target.value.replace(/[^0-9.]/g, ''));
                      setMontantTotal(cleaned);
                    }}
                    placeholder="0.00"
                    className="w-full pl-12 pr-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                    <Euro className="w-5 h-5 text-dark-400" />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section répartition */}
        <div className="space-y-6">
          <div className="bg-dark-900 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Répartition</h2>
              <button
                onClick={calculerMontantAutre}
                className="flex items-center space-x-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-primary-500 rounded-lg transition-colors duration-200"
              >
                <Calculator className="w-4 h-4" />
                <span className="text-sm font-medium">Calculer reste</span>
              </button>
            </div>
            
            {participants.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-dark-300 mb-4">Aucun participant disponible</p>
                <button
                  onClick={() => navigate('/participants')}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Ajouter des participants
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {participants.map(participant => (
                  <div key={participant.id} className="flex items-center justify-between p-4 bg-dark-800 rounded-lg">
                    <span className="text-white font-medium">{participant.nom}</span>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={participantsMontants[participant.id] || ''}
                        onChange={(e) => handleMontantChange(e.target.value, participant.id)}
                        placeholder="0.00"
                        className="w-24 px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-right placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <span className="text-white">€</span>
                    </div>
                  </div>
                ))}
                
                <div className="border-t border-dark-700 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-bold">Total payé:</span>
                    <span className={`text-lg font-bold ${
                      Math.abs(parseFloat(calculerTotal()) - parseFloat(montantTotal || '0')) > 0.01 
                        ? 'text-red-500' 
                        : 'text-primary-500'
                    }`}>
                      {calculerTotal()} €
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bouton d'enregistrement */}
      <div className="flex justify-end pt-6 border-t border-dark-800">
        <button
          onClick={handleSaveDepense}
          disabled={loading}
          className="flex items-center space-x-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-700 text-white rounded-lg font-medium transition-colors duration-200"
        >
          <Save className="w-5 h-5" />
          <span>Enregistrer</span>
        </button>
      </div>
    </div>
  );
};

export default AddSharedExpense; 